/**
 * Admin management types for the GDB Cloud Edition.
 *
 * These types match the Rust server's JSON response shapes from
 * /api/admin/* endpoints (see server/src/server/handlers/platform/admin.rs).
 */

/** Valid module IDs that can be granted/revoked. Matches backend VALID_MODULES. */
export const VALID_MODULES = [
  'oracle',
  'forge',
  'prism',
  'echo',
  'hoard',
  'ai',
  'character-creator',
  'agora',
  'atlas-painter',
] as const

export type ValidModuleId = (typeof VALID_MODULES)[number]

/** User summary in admin user list (GET /api/admin/users). */
export interface AdminUserInfo {
  id: string
  email: string
  name: string | null
  tier: string | null
  role: string | null
  isActive: boolean
  emailVerified: boolean
  ownedTools: string[]
  createdAt: string | null
  betaEnrolled: boolean
}

/** Paginated response from GET /api/admin/users. */
export interface AdminUserListResponse {
  users: AdminUserInfo[]
  total: number
  page: number
  limit: number
}

/** Purchase record within admin user detail. */
export interface AdminPurchase {
  id: number
  productType: string | null
  productId: string | null
  amountPaid: number | null
  stripePaymentIntentId: string | null
  createdAt: string | null
}

/** License activation record within admin user detail. */
export interface AdminActivation {
  licenseId: number
  deviceId: string | null
  isActive: number
  activatedAt: string | null
  lastCheckAt: string | null
}

/** Full user detail returned by GET /api/admin/users/:id. */
export interface AdminUserDetail {
  id: string
  email: string
  name: string | null
  role: string
  tier: string | null
  isActive: boolean
  emailVerified: boolean
  betaEnrolled: boolean
  ownedModules: string[]
  lastLoginAt: string | null
  createdAt: string | null
  updatedAt: string | null
  purchases: AdminPurchase[]
  activations: AdminActivation[]
  /**
   * Super-admin claim for the TARGET user. Optional + backwards-compatible:
   * older sidecars that predate emitting this on the user-detail payload
   * decode it as `undefined`, which the delete-user UI guard treats as
   * `false`. Used to HIDE the destructive "Delete user" button when the
   * target is a super-admin — mirroring the backend's 403 on
   * DELETE /api/cms/admin/users/:id. The backend remains authoritative;
   * this is defense-in-depth in the UI.
   */
  isSuperAdmin?: boolean
}

/** Admin note on a user (GET /api/admin/users/:id/notes). */
export interface AdminNote {
  id: number
  userId: string
  adminId: string
  adminName: string | null
  adminEmail: string
  note: string
  createdAt: string | null
}

/** Wrapper for GET /api/admin/users/:id/notes response. */
export interface AdminNotesResponse {
  notes: AdminNote[]
}

/** Platform analytics returned by GET /api/admin/analytics. */
export interface AdminAnalytics {
  totalUsers: number
  activeUsers: number
  betaUsers: number
  totalPurchases: number
  totalRevenue: number
  purchasesByProduct: Record<string, number>
}

// Phase 16 Plan 08: AdminProduct / AdminProductsResponse / AdminProductResponse
// / AdminProductSaveResponse / UpsertProductRequest interfaces deleted.
// The /console/products page uses ProductAdminListItem / ProductAdminDetail
// from @/core/console/controllers/queries/useProductsAdmin (Plan 16-03)
// against the new /api/cms/admin/products surface.

/** Query parameters for listing users. */
export interface ListUsersParams {
  page?: number
  limit?: number
  search?: string
}

/** Request body for POST /api/admin/users/:id/grant-module. */
export interface GrantModuleRequest {
  moduleId: string
}

/** Request body for POST /api/admin/users/:id/revoke-module. */
export interface RevokeModuleRequest {
  moduleId: string
}

/** Request body for POST /api/admin/users/:id/change-role. */
export interface ChangeRoleRequest {
  role: string
}

/** Standard success response from admin action endpoints. */
export interface AdminActionResponse {
  success: boolean
  message?: string
}
